import {
    useEffect,
    useState,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { SoftImage } from '@/components/site/soft-image';
import { cn } from '@/lib/utils';

const FADE_MS = 700;
const AUTO_MS = 5500;

export type FadeCoverSlide = {
    id: string;
    src: string;
    alt?: string;
};

/**
 * Stacked fading covers with optional autoplay, dots, and arrow controls.
 * When `overlay` is set (e.g. a card link), it sits above the images and
 * below the controls so they stay outside the link hit target.
 */
export function FadeCoverGallery({
    slides,
    className,
    imageClassName,
    width,
    height,
    sizes,
    fetchPriority,
    loading,
    showControls = true,
    showArrows = false,
    overlay,
    onActiveChange,
}: {
    slides: FadeCoverSlide[];
    className?: string;
    imageClassName?: string;
    width?: number;
    height?: number;
    sizes?: string;
    fetchPriority?: 'high' | 'low' | 'auto';
    loading?: 'eager' | 'lazy';
    showControls?: boolean;
    /** Prev/next chevrons - suited to large hero frames, not card thumbs. */
    showArrows?: boolean;
    /** Absolute-fill layer between images and dots (typically a link). */
    overlay?: ReactNode;
    onActiveChange?: (index: number) => void;
}) {
    const valid = slides.filter((s) => s.src);
    const [index, setIndex] = useState(0);
    const [loadExtras, setLoadExtras] = useState(false);
    const count = valid.length;
    const activeIndex = count === 0 ? 0 : Math.min(index, count - 1);
    const multi = count > 1;

    useEffect(() => {
        if (!multi) return;
        let cancelled = false;
        const start = () => {
            if (!cancelled) setLoadExtras(true);
        };
        window.addEventListener('pointerdown', start, { once: true, passive: true });
        const timeout = window.setTimeout(start, 6000);
        return () => {
            cancelled = true;
            window.removeEventListener('pointerdown', start);
            window.clearTimeout(timeout);
        };
    }, [multi]);

    useEffect(() => {
        onActiveChange?.(activeIndex);
    }, [activeIndex, onActiveChange]);

    useEffect(() => {
        if (count <= 1 || !loadExtras) return;
        if (
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
            return;
        }
        const id = window.setInterval(() => {
            setIndex((i) => (i + 1) % count);
        }, AUTO_MS);
        return () => window.clearInterval(id);
    }, [count, index, loadExtras]);

    if (count === 0) return null;

    function goTo(next: number, event?: MouseEvent) {
        event?.preventDefault();
        event?.stopPropagation();
        setLoadExtras(true);
        setIndex(next);
    }

    return (
        <div className={cn('relative', className)}>
            {valid.map((slide, i) => {
                if (i > 0 && !loadExtras) return null;
                const isActive = i === activeIndex;
                return (
                    <div
                        key={slide.id}
                        className={cn(
                            i === 0 ? 'relative' : 'absolute inset-0',
                            'transition-opacity ease-in-out',
                            isActive ? 'opacity-100' : 'pointer-events-none opacity-0',
                        )}
                        style={{ transitionDuration: `${FADE_MS}ms` }}
                        aria-hidden={!isActive}
                    >
                        <SoftImage
                            src={slide.src}
                            alt={slide.alt ?? ''}
                            width={width}
                            height={height}
                            sizes={sizes}
                            className={cn('size-full object-cover', imageClassName)}
                            fetchPriority={i === 0 ? fetchPriority : 'low'}
                            loading={i === 0 ? loading : 'lazy'}
                        />
                    </div>
                );
            })}

            {overlay}

            {showArrows && multi ? (
                <>
                    <button
                        type="button"
                        className="absolute start-2 top-1/2 z-10 inline-flex size-10 -translate-y-1/2 items-center justify-center bg-black/35 text-white/85 backdrop-blur-[2px] transition-colors hover:bg-black/55 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 sm:start-3 sm:size-11"
                        aria-label="תמונה קודמת"
                        onClick={(event) =>
                            goTo((activeIndex - 1 + count) % count, event)
                        }
                    >
                        <ChevronRight className="size-6" />
                    </button>
                    <button
                        type="button"
                        className="absolute end-2 top-1/2 z-10 inline-flex size-10 -translate-y-1/2 items-center justify-center bg-black/35 text-white/85 backdrop-blur-[2px] transition-colors hover:bg-black/55 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 sm:end-3 sm:size-11"
                        aria-label="תמונה הבאה"
                        onClick={(event) =>
                            goTo((activeIndex + 1) % count, event)
                        }
                    >
                        <ChevronLeft className="size-6" />
                    </button>
                </>
            ) : null}

            {showControls && multi && (
                <div
                    className="absolute inset-x-0 bottom-2 z-10 flex items-center justify-center gap-1.5 sm:bottom-3 sm:gap-2"
                    role="tablist"
                    aria-label="גלריית תמונות"
                >
                    {valid.map((slide, i) => {
                        const selected = i === activeIndex;
                        return (
                            <button
                                key={slide.id}
                                type="button"
                                role="tab"
                                aria-selected={selected}
                                aria-label={`תמונה ${i + 1} מתוך ${count}`}
                                onClick={(event) => goTo(i, event)}
                                className={cn(
                                    'group relative flex size-6 items-center justify-center rounded-full',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40',
                                )}
                            >
                                <span
                                    className={cn(
                                        'size-2 rounded-full border transition-colors',
                                        selected
                                            ? 'border-white bg-white'
                                            : 'border-white/50 bg-white/20 group-hover:border-white/80 group-hover:bg-white/40',
                                    )}
                                    aria-hidden
                                />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
